import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import api, { API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { useChartColors } from "@/lib/useChartColors";
import { cn } from "@/lib/utils";
import { FileCsv, FilePdf, CaretUpDown, Check } from "@phosphor-icons/react";

const monthNow = () => new Date().toISOString().slice(0, 7);
const ALL = "__all__";
const utilColor = (u) => (u >= 80 ? "text-rag-green-fg" : u >= 50 ? "text-rag-amber-fg" : "text-rag-red-fg");
const isoDay = (d) => d.toISOString().slice(0, 10);
const PERIODS = [
  { k: "month", l: "By month" },
  { k: "last_7", l: "Last 7 days" },
  { k: "last_30", l: "Last 30 days" },
  { k: "this_month", l: "This month" },
  { k: "last_month", l: "Last month" },
  { k: "this_quarter", l: "This quarter" },
  { k: "last_quarter", l: "Last quarter" },
  { k: "this_year", l: "This year" },
  { k: "custom", l: "Custom range" },
];
const periodRange = (period) => {
  const now = new Date();
  const y = now.getFullYear(), m = now.getMonth();
  switch (period) {
    case "this_month": return [isoDay(new Date(y, m, 1)), isoDay(new Date(y, m + 1, 0))];
    case "last_month": return [isoDay(new Date(y, m - 1, 1)), isoDay(new Date(y, m, 0))];
    case "this_quarter": { const q = Math.floor(m / 3); return [isoDay(new Date(y, q * 3, 1)), isoDay(new Date(y, q * 3 + 3, 0))]; }
    case "last_quarter": { const q = Math.floor(m / 3) - 1; return [isoDay(new Date(y, q * 3, 1)), isoDay(new Date(y, q * 3 + 3, 0))]; }
    case "this_year": return [isoDay(new Date(y, 0, 1)), isoDay(new Date(y, 11, 31))];
    case "last_7": { const s = new Date(now); s.setDate(s.getDate() - 6); return [isoDay(s), isoDay(now)]; }
    case "last_30": { const s = new Date(now); s.setDate(s.getDate() - 29); return [isoDay(s), isoDay(now)]; }
    default: return [null, null];
  }
};

export default function TimesheetReports() {
  const { user } = useAuth();
  // Only the dedicated timesheet_admin sees all teams (+ the Manager/Team filter). The ITSM
  // 'admin' role is team-scoped for reports, matching Approvals & the Team dashboard.
  const isAdmin = user?.role === "timesheet_admin";
  const [params, setParams] = useSearchParams();
  const [month, setMonth] = useState(params.get("tm") || monthNow());
  const [period, setPeriod] = useState(params.get("tp") || "month");
  const [customFrom, setCustomFrom] = useState(params.get("tfrom") || "");
  const [customTo, setCustomTo] = useState(params.get("tto") || "");
  const [managerId, setManagerId] = useState(params.get("tmgr") || ALL);
  const [userId, setUserId] = useState(params.get("tuser") || ALL);
  const [clientId, setClientId] = useState(params.get("tclient") || ALL);
  const [clients, setClients] = useState([]);
  const [roster, setRoster] = useState({ managers: [], users: [] });
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const chart = useChartColors();
  const didInit = useRef(false);

  useEffect(() => { api.get("/timesheet/clients").then((r) => setClients(r.data)).catch(() => {}); }, []);

  // Roster (team members + managers) — reloads when the admin picks a different team.
  useEffect(() => {
    const mp = managerId !== ALL ? `?manager_id=${managerId}` : "";
    api.get(`/timesheet/report/roster${mp}`).then((r) => setRoster(r.data)).catch(() => setRoster({ managers: [], users: [] }));
    if (didInit.current) setUserId(ALL);
    else didInit.current = true;
  }, [managerId]);

  // Keep the URL in sync so a shared link reopens the exact tab, period and team/user/client filters.
  useEffect(() => {
    setParams((prev) => {
      const p = new URLSearchParams(prev);
      const setOrDel = (k, v, def) => { if (v && v !== def) p.set(k, v); else p.delete(k); };
      setOrDel("tp", period, "month");
      setOrDel("tm", period === "month" ? month : "", "");
      setOrDel("tfrom", period === "custom" ? customFrom : "", "");
      setOrDel("tto", period === "custom" ? customTo : "", "");
      setOrDel("tmgr", managerId, ALL);
      setOrDel("tuser", userId, ALL);
      setOrDel("tclient", clientId, ALL);
      return p;
    }, { replace: true });
    // eslint-disable-next-line
  }, [period, month, customFrom, customTo, managerId, userId, clientId]);

  const periodParams = useCallback(() => {
    if (period === "month") return { month };
    if (period === "custom") {
      const p = {};
      if (customFrom) p.date_from = customFrom;
      if (customTo) p.date_to = customTo;
      return p;
    }
    const [df, dt] = periodRange(period);
    return { date_from: df, date_to: dt };
  }, [period, month, customFrom, customTo]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...periodParams() };
      if (managerId !== ALL) params.manager_id = managerId;
      if (userId !== ALL) params.user_id = userId;
      if (clientId !== ALL) params.client_id = clientId;
      const { data } = await api.get("/timesheet/report", { params });
      setReport(data);
    } finally { setLoading(false); }
  }, [periodParams, managerId, userId, clientId]);

  useEffect(() => { load(); }, [load]);

  const download = async (fmt) => {
    try {
      const params = new URLSearchParams();
      Object.entries(periodParams()).forEach(([k, v]) => { if (v) params.set(k, v); });
      if (managerId !== ALL) params.set("manager_id", managerId);
      if (userId !== ALL) params.set("user_id", userId);
      if (clientId !== ALL) params.set("client_id", clientId);
      const token = localStorage.getItem("helix_token");
      const res = await fetch(`${API}/timesheet/report/${fmt}?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const clientName = clientId !== ALL ? "-" + (clients.find((c) => c.id === clientId)?.name || "client").replace(/\s+/g, "_") : "";
      const periodTag = period === "month" ? month : period;
      const a = document.createElement("a");
      a.href = url;
      a.download = `timesheet-${periodTag}${userId !== ALL ? "-user" : "-all"}${clientName}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Could not export"); }
  };

  const userOptions = [{ value: ALL, label: "All users" }, ...(roster.users || []).map((u) => ({ value: u.id, label: u.name }))];
  const clientOptions = [{ value: ALL, label: "All clients" }, ...clients.map((c) => ({ value: c.id, label: c.name }))];
  const managerOptions = [{ value: ALL, label: "All teams" }, ...(roster.managers || []).map((m) => ({ value: m.id, label: m.name }))];

  const t = report?.totals || {};
  const userBars = (report?.by_user || []).map((u) => ({ name: u.user_name, hours: u.total_hours }));
  const clientBars = (report?.by_client || []).map((c) => ({ name: c.client, hours: c.hours }));
  const kraBars = (report?.by_kra || []).map((k) => ({ name: k.kra, hours: k.hours }));

  // Roll up per-user rows into per-manager (team) totals for the "Team utilisation" summary.
  const teamRollup = Object.values((report?.by_user || []).reduce((acc, u) => {
    const key = u.manager_name || "—";
    if (!acc[key]) acc[key] = { manager: key, members: 0, total: 0, billable: 0, approved: 0 };
    acc[key].members += 1;
    acc[key].total += u.total_hours || 0;
    acc[key].billable += u.billable_hours || 0;
    acc[key].approved += u.approved_hours || 0;
    return acc;
  }, {})).map((r) => ({
    ...r,
    total: Math.round(r.total * 10) / 10,
    billable: Math.round(r.billable * 10) / 10,
    approved: Math.round(r.approved * 10) / 10,
    utilization: r.total ? Math.round((100 * r.billable) / r.total) : 0,
  })).sort((a, b) => b.total - a.total);

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="timesheet-reports-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold">Timesheet Reports</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Consolidated hours across {report?.scope === "all" ? "all users" : "your team"} — by user, client and KRA, with late-submission tracking.
          </p>
        </div>
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <Label className="text-xs">Period</Label>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-40" data-testid="tsr-period-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PERIODS.map((p) => (<SelectItem key={p.k} value={p.k}>{p.l}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          {period === "month" && (
            <div>
              <Label className="text-xs">Month</Label>
              <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40" data-testid="tsr-month-input" />
            </div>
          )}
          {period === "custom" && (
            <>
              <div>
                <Label className="text-xs">From</Label>
                <Input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="w-40" data-testid="tsr-from-input" />
              </div>
              <div>
                <Label className="text-xs">To</Label>
                <Input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="w-40" data-testid="tsr-to-input" />
              </div>
            </>
          )}
          {isAdmin && (
            <div>
              <Label className="text-xs">Manager / Team</Label>
              <SearchSelect value={managerId} onChange={setManagerId} options={managerOptions}
                placeholder="Search manager…" testid="tsr-manager-select" width="w-56" />
            </div>
          )}
          <div>
            <Label className="text-xs">User</Label>
            <SearchSelect value={userId} onChange={setUserId} options={userOptions}
              placeholder="Search user…" testid="tsr-user-select" width="w-56" />
          </div>
          <div>
            <Label className="text-xs">Client</Label>
            <SearchSelect value={clientId} onChange={setClientId} options={clientOptions}
              placeholder="Search client…" testid="tsr-client-select" width="w-56" />
          </div>
          <Button variant="outline" onClick={() => download("csv")} data-testid="tsr-export-csv"><FileCsv size={16} className="mr-1.5" /> CSV</Button>
          <Button variant="outline" onClick={() => download("pdf")} data-testid="tsr-export-pdf"><FilePdf size={16} className="mr-1.5" /> PDF</Button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="tsr-kpis">
        <Card className="p-4"><div className="text-xs text-muted-foreground">Total hours</div>
          <div className="font-display text-2xl mt-1" data-testid="tsr-total-hours">{t.total_hours || 0}h</div></Card>
        <Card className="p-4"><div className="text-xs text-muted-foreground">Billable</div>
          <div className="font-display text-2xl mt-1 text-muted-foreground" data-testid="tsr-billable-hours">{t.billable_hours || 0}h</div></Card>
        <Card className="p-4" title="Billable hours ÷ total logged hours"><div className="text-xs text-muted-foreground">Utilization</div>
          <div className={`font-display text-2xl mt-1 ${utilColor(t.utilization)}`} data-testid="tsr-utilization">{t.utilization || 0}%</div></Card>
        <Card className="p-4"><div className="text-xs text-muted-foreground">Approved</div>
          <div className="font-display text-2xl mt-1 text-rag-green-fg">{t.approved_hours || 0}h</div></Card>
        <Card className="p-4"><div className="text-xs text-muted-foreground">Awaiting approval</div>
          <div className="font-display text-2xl mt-1 text-rag-amber-fg">{t.submitted_hours || 0}h</div></Card>
        <Card className="p-4"><div className="text-xs text-muted-foreground">Late submissions</div>
          <div className={`font-display text-2xl mt-1 ${t.late_count ? "text-rag-red-fg" : ""}`} data-testid="tsr-late-count">{t.late_count || 0}</div></Card>
      </div>

      {loading ? (
        <Card className="p-8 text-center text-sm text-muted-foreground">Loading…</Card>
      ) : (t.entry_count === 0) ? (
        <Card className="p-10 text-center text-sm text-muted-foreground" data-testid="tsr-empty">No timesheet entries for {month}.</Card>
      ) : (
        <>
          <div className="grid lg:grid-cols-2 gap-4">
            <ChartCard title="Hours by user" data={userBars} chart={chart} testid="tsr-chart-user" />
            <ChartCard title="Hours by client" data={clientBars} chart={chart} testid="tsr-chart-client" />
          </div>
          <ChartCard title="Hours by KRA" data={kraBars} chart={chart} testid="tsr-chart-kra" />

          {/* Team utilisation rollup — one card per approving manager */}
          {teamRollup.length > 0 && (
            <Card className="overflow-hidden" data-testid="tsr-team-rollup">
              <div className="px-4 py-3 text-xs font-mono uppercase tracking-wider text-muted-foreground border-b border-border">Team utilisation by manager</div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border">
                {teamRollup.map((r) => (
                  <div key={r.manager} className="bg-card p-4" data-testid={`tsr-team-card-${r.manager}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-medium truncate">{r.manager}</div>
                      <span className="text-[10px] font-mono uppercase text-muted-foreground shrink-0">{r.members} {r.members === 1 ? "member" : "members"}</span>
                    </div>
                    <div className="mt-3 flex items-end justify-between">
                      <div>
                        <div className="text-[10px] font-mono uppercase text-muted-foreground">Team hours</div>
                        <div className="font-display text-2xl mt-0.5" data-testid={`tsr-team-hours-${r.manager}`}>{r.total}h</div>
                      </div>
                      <div className="text-right">
                        <div className="text-[10px] font-mono uppercase text-muted-foreground">Utilisation</div>
                        <div className={`font-display text-2xl mt-0.5 ${utilColor(r.utilization)}`} data-testid={`tsr-team-util-${r.manager}`}>{r.utilization}%</div>
                      </div>
                    </div>
                    <div className="mt-2 text-[11px] text-muted-foreground">{r.billable}h billable · {r.approved}h approved</div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Consolidated per-user table */}
          <Card className="overflow-hidden" data-testid="tsr-user-table">
            <div className="px-4 py-3 text-xs font-mono uppercase tracking-wider text-muted-foreground border-b border-border">Per-user breakdown</div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Manager</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="text-right">Billable</TableHead>
                  <TableHead className="text-right">Utilization</TableHead>
                  <TableHead className="text-right">Approved</TableHead>
                  <TableHead className="text-right">Awaiting</TableHead>
                  <TableHead className="text-right">Late</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(report?.by_user || []).map((u) => (
                  <TableRow key={u.user_id} data-testid={`tsr-user-row-${u.user_id}`}>
                    <TableCell className="font-medium">{u.user_name}</TableCell>
                    <TableCell className="text-muted-foreground" data-testid={`tsr-user-mgr-${u.user_id}`}>{u.manager_name || "—"}</TableCell>
                    <TableCell className="text-right font-mono">{u.total_hours}h</TableCell>
                    <TableCell className="text-right font-mono text-muted-foreground">{u.billable_hours}h</TableCell>
                    <TableCell className={`text-right font-mono font-semibold ${utilColor(u.utilization)}`} data-testid={`tsr-user-util-${u.user_id}`}>{u.utilization}%</TableCell>
                    <TableCell className="text-right font-mono text-rag-green-fg">{u.approved_hours}h</TableCell>
                    <TableCell className="text-right font-mono text-rag-amber-fg">{u.submitted_hours}h</TableCell>
                    <TableCell className="text-right">
                      {u.late_count ? <Badge variant="outline" className="bg-rag-red-bg text-rag-red-fg border-rag-red/30">{u.late_count}</Badge> : <span className="text-muted-foreground">0</span>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </>
      )}
    </div>
  );
}

function SearchSelect({ value, onChange, options, placeholder, testid, width = "w-52" }) {
  const [open, setOpen] = useState(false);
  const sel = options.find((o) => o.value === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" aria-expanded={open} data-testid={testid} className={cn(width, "justify-between font-normal")}>
          <span className="truncate">{sel ? sel.label : placeholder}</span>
          <CaretUpDown size={14} className="ml-2 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className={cn(width, "p-0")} align="start">
        <Command>
          <CommandInput placeholder={placeholder} className="h-9" data-testid={`${testid}-input`} />
          <CommandList>
            <CommandEmpty>No match.</CommandEmpty>
            <CommandGroup>
              {options.map((o) => (
                <CommandItem key={o.value} value={o.label} data-testid={`${testid}-opt-${o.value}`}
                  onSelect={() => { onChange(o.value); setOpen(false); }}>
                  <Check size={14} className={cn("mr-2", value === o.value ? "opacity-100" : "opacity-0")} />
                  <span className="truncate">{o.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function ChartCard({ title, data, chart, testid }) {
  return (
    <Card className="p-4" data-testid={testid}>
      <div className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-3">{title}</div>
      {data.length === 0 ? (
        <div className="h-[220px] grid place-items-center text-sm text-muted-foreground">No data</div>
      ) : (
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={data} margin={{ top: 4, right: 8, left: -12, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} vertical={false} />
            <XAxis dataKey="name" tick={{ fill: chart.axis, fontSize: 11 }} interval={0} angle={-12} textAnchor="end" height={50} />
            <YAxis tick={{ fill: chart.axis, fontSize: 11 }} />
            <Tooltip contentStyle={chart.tooltip} cursor={{ fill: "rgba(255,203,7,0.08)" }} />
            <Bar dataKey="hours" fill="#FFCB07" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      )}
    </Card>
  );
}
